import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { Box, Stack } from "@mui/material";
import { Alert } from "Components/_Shared/Alert";
import { Button } from "Components/_Shared/Buttons/Button";
import type { ClientCardRowAction } from "Components/_Shared/Client/ClientCardDetail";
import { Loader } from "Components/_Shared/Loader";
import { useClientDetail, useCreateQuestionnaireSignRequest, useGetAnalysis } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemRelationType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, ClientCardState, getClientCardState, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";
import { useContext, useState } from "react";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { FamilyMemberDrawer } from "../Family/FamilyMemberDrawer";
import { ClientCard } from "../_Shared/Client/ClientCard";
import { useQuestionnaireContext } from "./QuestionnaireContext";
import { QuestionnaireEvaluationVerifyModal } from "./QuestionnaireEvaluationVerifyModal";

type Props = {
  signedAt?: string | null;
  onVerified: () => void;
};

export const QuestionnaireEvaluationVerify = ({ signedAt, onVerified }: Props) => {
  const { versionId, analysisId } = useContext(AnalysisDetailContext);
  const { isReadOnly } = useQuestionnaireContext();
  const { t } = useResource();

  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { mutateAsync: createSignRequest, isPending: isSendingSMS } = useCreateQuestionnaireSignRequest();
  const { data: analysisData, isPending: isAnalysisPending, isError: isAnalysisError } = useGetAnalysis(analysisId, versionId);
  const {
    data: clientData,
    isPending: isClientPending,
    isError: isClientError,
  } = useClientDetail(analysisData?.clientGuid ?? "", {
    query: { select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });

  if (isAnalysisPending || isClientPending) return <Loader />;

  if (isAnalysisError || isClientError || !analysisData) {
    return <Alert variant="error" text={t(Resources.Clients.LoadError)} />;
  }

  const clientGuid = analysisData.clientGuid;
  const isLocalClient = clientData?.type === "local";
  const mangoUpCardUrl = clientData && "mangoUpCardUrl" in clientData ? clientData.mangoUpCardUrl : undefined;
  const canOpenMangoUpCard = Boolean(mangoUpCardUrl);
  const cardState = clientData ? getClientCardState(clientData) : undefined;
  const hasInvalidMangoCard = isLocalClient || cardState === ClientCardState.Missing || cardState === ClientCardState.Unsigned;

  const handleSendSMS = async () => {
    try {
      const response = await createSignRequest({ analysisId, versionId });
      setVerificationToken(response.verificationToken);
      setSmsModalOpen(true);
    } catch {
      toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
    }
  };

  const clientCardRows: ClientCardRowAction[] = [
    {
      title: t(Resources.AnalysisDetail.TabMenu.InvestmentQuestionnaire),
      tag: signedAt ? { label: t(Resources.Clients.Detail.Signed), color: "success", variant: "solid" } : undefined,
      button: !signedAt
        ? {
            label: t(Resources.Clients.Detail.SendSMSCode),
            onClick: handleSendSMS,
            disabled: isSendingSMS || hasInvalidMangoCard || isReadOnly,
          }
        : undefined,
    },
  ];

  return (
    <>
      <Stack pl={Spacing.XXL} spacing={Spacing.L}>
        {hasInvalidMangoCard && (
          <Alert
            variant="warning"
            title={isLocalClient ? t(Resources.Clients.MangoNotLinkedTitle) : undefined}
            text={
              isLocalClient
                ? t(Resources.Clients.MangoNotLinkedText)
                : cardState === ClientCardState.Missing
                  ? t(Resources.Clients.MangoCardMissing)
                  : t(Resources.Clients.MangoCardNeedCheck)
            }
            action={
              isLocalClient ? (
                <Button variant="outlined" size="small" color="warning" onClick={() => setDrawerOpen(true)}>
                  {t(Resources.Common.Edit)}
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={!canOpenMangoUpCard}
                  onClick={() => {
                    if (!mangoUpCardUrl) return;
                    window.open(mangoUpCardUrl, "_blank", "noopener,noreferrer");
                  }}
                  endAdornment={<LaunchOutlinedIcon fontSize="small" />}
                >
                  {t(Resources.Clients.UpdateInMango)}
                </Button>
              )
            }
          />
        )}
        <ClientCard clientGuid={clientGuid} rows={clientCardRows} />
      </Stack>
      <QuestionnaireEvaluationVerifyModal
        isOpen={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        onConfirm={() => {
          setSmsModalOpen(false);
          onVerified();
        }}
        verificationToken={verificationToken}
      />
      {clientGuid && (
        <FamilyMemberDrawer
          clientGuid={clientGuid}
          clientRelationType={AnalysisClientItemRelationType.Client}
          open={drawerOpen}
          anchor="right"
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
};
